import {
    ActionIcon,
    Button,
    Group,
    Modal,
    Paper,
    SimpleGrid,
    Stack,
    Text,
    Title,
} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {IconCalendar, IconClock, IconEdit, IconPlus, IconTrash} from '@tabler/icons-react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useEditRights} from './edit-rights-context'
import type {JobDataLayer} from './job-data-layer'
import {parseApiDate, toApiDateString, toApiTimeString, toLocalDate} from './job-schedule-format'
import {formatTimeEntryRange} from './job-time-entry-format'
import {validateTimeEntryInput} from './job-time-entry-validation'
import {JobTimePicker} from './job-time-picker'
import type {TimeEntry, WorkerAssignmentTimeEntries} from './schemas'
import type {JobDetail} from './types'

type JobTimeEntriesSectionProps = {
    job: JobDetail
    jobDataLayer: JobDataLayer
    onJobUpdated: (job: JobDetail) => void
}

/** Stable key so time entries reload when worker assignments change on the same job. */
export function workerAssignmentSyncKey(job: JobDetail): string {
    return job.assignments
        .filter((assignment) => assignment.workerId != null)
        .map((assignment) => `${assignment.id}:${assignment.workerId}`)
        .sort()
        .join('|')
}

type TimeEntryFormValues = {
    startDate: Date | null
    startTime: string | null
    endDate: Date | null
    endTime: string | null
}

function parseDateInputValue(value: string | Date | null): Date | null {
    return value == null ? null : toLocalDate(value)
}

function defaultFormValues(job: JobDetail): TimeEntryFormValues {
    return {
        startDate: parseApiDate(job.startDate),
        startTime: job.startTime,
        endDate: job.endDate ? parseApiDate(job.endDate) : parseApiDate(job.startDate),
        endTime: job.endTime ?? job.startTime,
    }
}

function entryToFormValues(entry: TimeEntry): TimeEntryFormValues {
    return {
        startDate: parseApiDate(entry.startDate),
        startTime: entry.startTime,
        endDate: parseApiDate(entry.endDate),
        endTime: entry.endTime,
    }
}

function formValuesToInput(values: TimeEntryFormValues) {
    if (!values.startDate || !values.startTime || !values.endDate || !values.endTime) {
        throw new Error('Time entry is incomplete')
    }

    const startTime = toApiTimeString(values.startTime)
    const endTime = toApiTimeString(values.endTime)
    if (!startTime || !endTime) {
        throw new Error('Time entry times are invalid')
    }

    return {
        startDate: toApiDateString(values.startDate),
        startTime,
        endDate: toApiDateString(values.endDate),
        endTime,
    }
}

type TimeEntryModalProps = {
    opened: boolean
    workerName: string
    title: string
    initialValues: TimeEntryFormValues
    saving: boolean
    errorMessage: string | null
    fieldErrors: Record<string, string>
    onClose: () => void
    onSave: (values: TimeEntryFormValues) => void
    onValuesChange: (values: TimeEntryFormValues) => void
}

function TimeEntryModal({
    opened,
    workerName,
    title,
    initialValues,
    saving,
    errorMessage,
    fieldErrors,
    onClose,
    onSave,
    onValuesChange,
}: TimeEntryModalProps) {
    const [values, setValues] = useState(initialValues)

    useEffect(() => {
        if (opened) {
            setValues(initialValues)
        }
    }, [initialValues, opened])

    function updateValues(nextValues: TimeEntryFormValues) {
        setValues(nextValues)
        onValuesChange(nextValues)
    }

    return (
        <Modal opened={opened} onClose={onClose} title={title}>
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    Worker: {workerName}
                </Text>

                <SimpleGrid cols={{base: 1, sm: 2}} spacing="md">
                    <DateInput
                        label="Start date"
                        value={values.startDate}
                        onChange={(value) =>
                            updateValues({
                                ...values,
                                startDate: parseDateInputValue(value),
                            })
                        }
                        leftSection={<IconCalendar size={16} />}
                        error={fieldErrors.startDate}
                        required
                    />
                    <JobTimePicker
                        label="Start time"
                        value={values.startTime}
                        onChange={(startTime) => updateValues({...values, startTime})}
                        leftSection={<IconClock size={16} />}
                        error={fieldErrors.startTime}
                        required
                    />
                    <DateInput
                        label="End date"
                        value={values.endDate}
                        onChange={(value) =>
                            updateValues({
                                ...values,
                                endDate: parseDateInputValue(value),
                            })
                        }
                        leftSection={<IconCalendar size={16} />}
                        error={fieldErrors.endDate}
                        required
                    />
                    <JobTimePicker
                        label="End time"
                        value={values.endTime}
                        onChange={(endTime) => updateValues({...values, endTime})}
                        leftSection={<IconClock size={16} />}
                        error={fieldErrors.endTime}
                        required
                    />
                </SimpleGrid>

                {errorMessage ? (
                    <Text c="red" size="sm">
                        {errorMessage}
                    </Text>
                ) : null}

                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button loading={saving} onClick={() => onSave(values)}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}

export function JobTimeEntriesSection({
    job,
    jobDataLayer,
    onJobUpdated,
}: JobTimeEntriesSectionProps) {
    const {canEdit, canDispatch} = useEditRights()
    const canLogTime = canEdit && canDispatch
    const [groups, setGroups] = useState<WorkerAssignmentTimeEntries[]>([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)
    const loadRequestId = useRef(0)

    const [modalState, setModalState] = useState<
        | {mode: 'create'; assignmentId: string; workerName: string}
        | {mode: 'edit'; assignmentId: string; workerName: string; entry: TimeEntry}
        | null
    >(null)
    const [formValues, setFormValues] = useState<TimeEntryFormValues>(() => defaultFormValues(job))
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
    const [saveError, setSaveError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null)
    const workerAssignmentKey = useMemo(() => workerAssignmentSyncKey(job), [job.assignments])

    const loadTimeEntries = useCallback(async () => {
        const requestId = ++loadRequestId.current
        setLoading(true)
        setLoadError(null)

        try {
            const loadedGroups = await jobDataLayer.listTimeEntries(job.id)
            if (requestId !== loadRequestId.current) {
                return
            }
            setGroups(loadedGroups)
        } catch (error) {
            if (requestId !== loadRequestId.current) {
                return
            }
            setLoadError(error instanceof Error ? error.message : 'Failed to load time entries')
        } finally {
            if (requestId === loadRequestId.current) {
                setLoading(false)
            }
        }
    }, [job.id, jobDataLayer])

    useEffect(() => {
        void loadTimeEntries()
    }, [loadTimeEntries, workerAssignmentKey])

    function openCreateModal(assignmentId: string, workerName: string) {
        setFormValues(defaultFormValues(job))
        setFieldErrors({})
        setSaveError(null)
        setModalState({mode: 'create', assignmentId, workerName})
    }

    function openEditModal(group: WorkerAssignmentTimeEntries, entry: TimeEntry) {
        setFormValues(entryToFormValues(entry))
        setFieldErrors({})
        setSaveError(null)
        setModalState({
            mode: 'edit',
            assignmentId: group.assignmentId,
            workerName: group.workerName,
            entry,
        })
    }

    function closeModal() {
        setModalState(null)
        setFieldErrors({})
        setSaveError(null)
    }

    async function handleSave(values: TimeEntryFormValues) {
        const validationErrors = validateTimeEntryInput(values)
        if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors)
            return
        }

        if (!modalState) {
            return
        }

        setSaving(true)
        setSaveError(null)
        setFieldErrors({})

        try {
            const input = formValuesToInput(values)
            const updatedJob =
                modalState.mode === 'create'
                    ? await jobDataLayer.createTimeEntry(job.id, modalState.assignmentId, input)
                    : await jobDataLayer.updateTimeEntry(job.id, modalState.entry.id, input)

            onJobUpdated(updatedJob)
            closeModal()
            await loadTimeEntries()
        } catch (error) {
            setSaveError(error instanceof Error ? error.message : 'Failed to save time entry')
        } finally {
            setSaving(false)
        }
    }

    async function handleDelete(entryId: string) {
        setDeletingEntryId(entryId)
        setLoadError(null)

        try {
            const updatedJob = await jobDataLayer.deleteTimeEntry(job.id, entryId)
            onJobUpdated(updatedJob)
            await loadTimeEntries()
        } catch (error) {
            setLoadError(error instanceof Error ? error.message : 'Failed to delete time entry')
        } finally {
            setDeletingEntryId(null)
        }
    }

    const hasWorkerAssignments = groups.length > 0

    return (
        <Paper p="md" withBorder>
            <Stack gap="md">
                <Title order={3}>Time entries</Title>

                {loading ? <Text c="dimmed">Loading time entries…</Text> : null}
                {loadError ? (
                    <Text c="red" size="sm">
                        {loadError}
                    </Text>
                ) : null}

                {!loading && !hasWorkerAssignments ? (
                    <Text c="dimmed" size="sm">
                        Assign workers before logging time.
                    </Text>
                ) : null}

                {!loading && hasWorkerAssignments
                    ? groups.map((group) => (
                          <Stack key={group.assignmentId} gap="xs">
                              <Group justify="space-between" align="center">
                                  <Text fw={600}>{group.workerName}</Text>
                                  {canLogTime ? (
                                      <Button
                                          leftSection={<IconPlus size={16} />}
                                          size="compact-sm"
                                          variant="light"
                                          onClick={() =>
                                              openCreateModal(group.assignmentId, group.workerName)
                                          }
                                      >
                                          Log time
                                      </Button>
                                  ) : null}
                              </Group>

                              {group.entries.length === 0 ? (
                                  <Text c="dimmed" size="sm">
                                      No time logged yet.
                                  </Text>
                              ) : (
                                  group.entries.map((entry) => (
                                      <Group
                                          key={entry.id}
                                          justify="space-between"
                                          align="flex-start"
                                          wrap="nowrap"
                                      >
                                          <Text size="sm">{formatTimeEntryRange(entry)}</Text>
                                          {canLogTime ? (
                                              <Group gap="xs" wrap="nowrap">
                                                  <ActionIcon
                                                      variant="subtle"
                                                      aria-label={`Edit time entry for ${group.workerName}`}
                                                      onClick={() => openEditModal(group, entry)}
                                                  >
                                                      <IconEdit size={16} />
                                                  </ActionIcon>
                                                  <ActionIcon
                                                      variant="subtle"
                                                      color="red"
                                                      aria-label={`Delete time entry for ${group.workerName}`}
                                                      loading={deletingEntryId === entry.id}
                                                      onClick={() => void handleDelete(entry.id)}
                                                  >
                                                      <IconTrash size={16} />
                                                  </ActionIcon>
                                              </Group>
                                          ) : null}
                                      </Group>
                                  ))
                              )}
                          </Stack>
                      ))
                    : null}
            </Stack>

            {modalState ? (
                <TimeEntryModal
                    opened
                    workerName={modalState.workerName}
                    title={modalState.mode === 'create' ? 'Log time' : 'Edit time entry'}
                    initialValues={formValues}
                    saving={saving}
                    errorMessage={saveError}
                    fieldErrors={fieldErrors}
                    onClose={closeModal}
                    onSave={(values) => void handleSave(values)}
                    onValuesChange={setFormValues}
                />
            ) : null}
        </Paper>
    )
}
